/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import BrandMarkIcon from './brand-mark-icon';
import BrandPillars from './brand-pillars';
import FooterColumnSlots from './footer-column-slots';
import LegalLinks from './legal-links';
import NavColumns from './nav-columns';
import SocialIcons from './social-icons';
import ValuePropsBanner from './value-props-banner';

interface MainFooterProps {
    paymentBadgesPage?: Promise<PageWithComponentData | null>;
    /** Business-Manager-editable addition to the Branding & Social Grid (pageId "sfnext-footer-column"). */
    slotFooterColumn?: Promise<PageWithComponentData | null>;
    /** Business-Manager-editable column, additive alongside NavColumns' "account" column (pageId "sfnext-footer-account"). */
    slotFooterAccount?: Promise<PageWithComponentData | null>;
    /** Business-Manager-editable column, additive alongside NavColumns' "customerSupport" column (pageId "sfnext-footer-support"). */
    slotFooterSupport?: Promise<PageWithComponentData | null>;
    /** Business-Manager-editable column, additive alongside NavColumns' "about" column (pageId "sfnext-footer-about"). */
    slotFooterAbout?: Promise<PageWithComponentData | null>;
    /** Business-Manager-editable column, additive alongside NavColumns' "contactUs" column (pageId "sfnext-footer-contact"). */
    slotFooterContact?: Promise<PageWithComponentData | null>;
    /** Stable loader promise: true when any desktop footer slot page exists. */
    footerSlotsAny?: Promise<boolean>;
    /** Business-Manager-editable override for LegalLinks (pageId "sfnext-footer-copy"). */
    slotFooterCopy?: Promise<PageWithComponentData | null>;
}

function FooterNavSwitch({
    footerSlotsAny,
    paymentBadgesPage,
    slotFooterAccount,
    slotFooterSupport,
    slotFooterAbout,
    slotFooterContact,
}: {
    footerSlotsAny?: Promise<boolean>;
    paymentBadgesPage?: Promise<PageWithComponentData | null>;
    slotFooterAccount?: Promise<PageWithComponentData | null>;
    slotFooterSupport?: Promise<PageWithComponentData | null>;
    slotFooterAbout?: Promise<PageWithComponentData | null>;
    slotFooterContact?: Promise<PageWithComponentData | null>;
}): ReactElement {
    // Keep this boundary free of <Await resolve={Promise.all(...)}/> in render.
    // Recreating that combined promise per render can cause repeated suspense retries.
    const hasAnyFooterSlot = use(footerSlotsAny ?? Promise.resolve(false));

    return hasAnyFooterSlot ? (
        <FooterColumnSlots
            slotFooterAccount={slotFooterAccount}
            slotFooterSupport={slotFooterSupport}
            slotFooterAbout={slotFooterAbout}
            slotFooterContact={slotFooterContact}
        />
    ) : (
        <NavColumns paymentBadgesPage={paymentBadgesPage} />
    );
}

export default function MainFooter({
    paymentBadgesPage,
    slotFooterColumn,
    slotFooterAccount,
    slotFooterSupport,
    slotFooterAbout,
    slotFooterContact,
    footerSlotsAny,
    slotFooterCopy,
}: MainFooterProps): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <footer className="mt-auto footer-container">
            <ValuePropsBanner />

            {/* Branding & Social Grid */}
            <div className="bg-footer-background py-16">
                <div className="section-container flex flex-col items-center text-center">
                    {/* Top Brand Section */}
                    <BrandMarkIcon className="w-10 h-10 text-header-accent-background" />
                    <p className="mt-3 text-4xl leading-11 font-stjude font-light text-header-accent-background">
                        {t('tagline')}
                        <sup className="ml-0.5">&reg;</sup>
                    </p>

                    <Region
                        page={slotFooterColumn ?? null}
                        regionId="content"
                        className="social-container"
                        errorElement={
                            <>
                                {/* Middle Connect Section */}
                                <div className="mt-10 flex flex-col items-center gap-4">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
                                        {t('connect.title')}
                                    </h3>
                                    <SocialIcons />
                                </div>

                                {/* Bottom Button Section */}
                                <a
                                    href="/signupupdates.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-10 inline-flex items-center justify-center rounded-full bg-brand-royal-blue px-10 py-4 text-sm font-medium text-white transition-opacity hover:opacity-90">
                                    {t('connect.emailUpdatesCta')}
                                </a>
                            </>
                        }
                    />
                </div>
            </div>

            <BrandPillars />

            <div className="bg-footer-background py-12 section-container footer-nav-columns">
                <Suspense fallback={<NavColumns paymentBadgesPage={paymentBadgesPage} />}>
                    <FooterNavSwitch
                        footerSlotsAny={footerSlotsAny}
                        paymentBadgesPage={paymentBadgesPage}
                        slotFooterAccount={slotFooterAccount}
                        slotFooterSupport={slotFooterSupport}
                        slotFooterAbout={slotFooterAbout}
                        slotFooterContact={slotFooterContact}
                    />
                </Suspense>
            </div>

            <div className="bg-footer-legal-background text-footer-legal-foreground py-6">
                <div className="section-container flex flex-col items-center gap-4 text-sm text-center copyright">
                    <Region
                        page={slotFooterCopy ?? null}
                        regionId="content"
                        errorElement={
                            <>
                                <div>
                                    © {new Date().getFullYear()} {t('copyright')}
                                </div>
                                <div className="flex flex-col items-center sm:flex-row gap-4">
                                    <LegalLinks />
                                </div>
                            </>
                        }
                    />
                </div>
            </div>
        </footer>
    );
}
